import { ExtractionLocation, ExtractedRecord, ExtractedRecordError, TargetSchema, ValidAny } from "@/types";

// =============================================================================
// Types
// =============================================================================

export interface CellHighlight {
  status: "extracted" | "error" | "warning" | "unmapped";
  fields: Array<{
    targetField: string;
    targetSchema: TargetSchema;
    extractedValue: ValidAny;
  }>;
  errors: Array<{
    message: string;
    severity: "error" | "warning";
  }>;
}

/** Key format: "row:col" (both 0-indexed into sheetData) */
export type CellHighlightMap = Map<string, CellHighlight>;

export type RowIndexMode = "absolute" | "relative";

// =============================================================================
// Header Utilities
// =============================================================================

/** Normalize a header string for comparison: lowercase, trim, collapse whitespace */
export const normalizeHeader = (header: string): string => {
  return String(header).toLowerCase().trim().replace(/\s+/g, " ");
};

/**
 * Build a map from normalized header name → column index.
 * Uses the header row from the sheet data, optionally cross-referencing
 * with the backend's detected_headers for better matching.
 */
export const buildHeaderColumnMap = (headerRow: ValidAny[], _detectedHeaders?: string[]): Map<string, number> => {
  const map = new Map<string, number>();
  for (let col = 0; col < headerRow.length; col++) {
    const cellValue = headerRow[col];
    if (cellValue !== null && cellValue !== undefined && String(cellValue).trim() !== "") {
      map.set(normalizeHeader(String(cellValue)), col);
    }
  }
  return map;
};

/**
 * Find the header row index in sheet data by matching against detected headers.
 * Scans each row and counts how many cells match the detected headers.
 * Returns the row with the highest match count.
 */
export const findHeaderRowIndex = (sheetData: ValidAny[][], detectedHeaders: string[]): number => {
  if (!detectedHeaders || detectedHeaders.length === 0 || sheetData.length === 0) {
    return 0;
  }

  const normalizedDetected = new Set(detectedHeaders.map(normalizeHeader));
  let bestRow = 0;
  let bestScore = 0;

  // Only scan the first 30 rows — headers are always near the top
  const scanLimit = Math.min(sheetData.length, 30);

  for (let rowIdx = 0; rowIdx < scanLimit; rowIdx++) {
    const row = sheetData[rowIdx];
    if (!row) continue;

    let score = 0;
    for (const cell of row) {
      if (cell !== null && cell !== undefined && normalizedDetected.has(normalizeHeader(String(cell)))) {
        score++;
      }
    }

    if (score > bestScore) {
      bestScore = score;
      bestRow = rowIdx;
    }
  }

  return bestRow;
};

// =============================================================================
// Cell Highlight Map Builder
// =============================================================================

/** Resolves an extraction source to a sheet column index. */
export type ColumnResolver = (source: { key?: string; coordinates?: Array<{ x: number; y: number }> }) => number | null;

/**
 * Build a column resolver over a header map, memoised by source key.
 *
 * The same source keys recur across every record (e.g. 500k records each
 * carrying "Member ID" -> column 3), so resolutions are cached. A windowed
 * caller should hold ONE resolver across windows rather than rebuilding it per
 * scroll -- that is why this is hoisted out of the map builder (#6755).
 *
 * Deliberate behaviour change, called out because it is not a pure refactor:
 * the previous inline cache returned a cached `null` immediately, so the FIRST
 * record with an unresolvable key fell through to `coordinates` and every later
 * one did not. The result therefore depended on iteration order, which is not
 * survivable once rows are built one window at a time. A cached `null` now
 * falls through to coordinates exactly as the first occurrence does.
 */
export const createColumnResolver = (headerColumnMap: Map<string, number>): ColumnResolver => {
  const columnCache = new Map<string, number | null>();

  return (source) => {
    if (!source) return null;

    if (source.key) {
      let col = columnCache.get(source.key);
      if (col === undefined) {
        col = headerColumnMap.get(normalizeHeader(source.key)) ?? null;
        columnCache.set(source.key, col);
      }
      if (col !== null) return col;
    }

    // Fall back to coordinates (not cacheable -- per-field)
    if (source.coordinates && source.coordinates.length > 0) {
      return source.coordinates[0].x;
    }

    return null;
  };
};

/** Sheet row index -> the records that landed on that row. */
export type SheetRowIndex = Map<number, ExtractedRecord[]>;

/**
 * Index records by the sheet row they occupy.
 *
 * This is the only full pass over the record set the review surface needs. It
 * is O(records) with one pointer write each, where `buildCellHighlightMap` is
 * O(records x fields) and allocates a `CellHighlight` plus two arrays and a map
 * entry per cell. For the 497,711-record motivating document that is the
 * difference between an index and ~497k live highlight objects pinned in a
 * `useMemo` (#6755).
 *
 * `rowCount` is `sheetData.length`. Records resolving outside the sheet are
 * dropped here so the windowed path reproduces `buildCellHighlightMap`'s bounds
 * check without needing the sheet itself.
 */
/**
 * The highest sheet row a SheetRowIndex covers, or null when it covers none
 * (#6900 part B).
 *
 * Read off the INDEX rather than the record list on purpose. The index is what
 * `highlightsFor` consults, so deriving coverage from anything else is a second
 * answer to one question — and the two would disagree the moment a record
 * resolves to no sheet row (buildSheetRowIndex drops those).
 *
 * Extracted from the hook so it can be tested. It was inline, and a mutation
 * replacing the whole derivation with a constant survived every test —
 * the drawer's tests inject this value as a prop, so nothing exercised the
 * derivation at all.
 */
export const maxIndexedSheetRow = (index: SheetRowIndex): number | null => {
  let max = -1;
  for (const row of index.keys()) if (row > max) max = row;
  return max >= 0 ? max : null;
};

export const buildSheetRowIndex = (
  extractedRecords: ExtractedRecord[],
  headerRowIndex: number,
  rowCount: number,
  rowIndexMode: RowIndexMode = "relative"
): SheetRowIndex => {
  const index: SheetRowIndex = new Map();
  if (!extractedRecords) return index;

  for (const record of extractedRecords) {
    if (!record.extracted_fields) continue;

    const sheetRow = resolveSheetRow(record, headerRowIndex, rowIndexMode);
    if (sheetRow === null || sheetRow < 0 || sheetRow >= rowCount) continue;

    const existing = index.get(sheetRow);
    if (existing) {
      existing.push(record);
    } else {
      index.set(sheetRow, [record]);
    }
  }

  return index;
};

/**
 * Build the cell highlight map for a contiguous range of sheet rows.
 *
 * `startRow`/`endRow` are inclusive 0-based indices into `sheetData`. Only
 * records indexed to rows in that range are visited, so cost scales with the
 * window on screen rather than with the size of the document.
 *
 * The output is identical to the corresponding subset of
 * `buildCellHighlightMap`'s result. That is asserted directly rather than
 * assumed: the tests union every window of a small document and compare it to
 * the full-set build (AC2).
 */
export const buildCellHighlightMapForRowRange = (
  sheetData: ValidAny[][],
  rowIndex: SheetRowIndex,
  resolveColumn: ColumnResolver,
  headerColumnMap: Map<string, number>,
  startRow: number,
  endRow: number,
  targetToSourceMap?: Map<string, string>
): CellHighlightMap => {
  const map: CellHighlightMap = new Map();

  if (rowIndex.size === 0 || sheetData.length === 0) return map;

  const from = Math.max(0, startRow);
  const to = Math.min(endRow, sheetData.length - 1);

  for (let row = from; row <= to; row++) {
    const records = rowIndex.get(row);
    if (!records) continue;
    for (const record of records) {
      applyRecordHighlights(map, record, row, sheetData, headerColumnMap, resolveColumn, targetToSourceMap);
    }
  }

  return map;
};

/**
 * Build the cell highlight map for the WHOLE record set.
 *
 * Kept as the reference implementation, and for callers holding a small fully
 * materialised record set. The review surface uses
 * `buildCellHighlightMapForRowRange` instead -- see #6755.
 */
export const buildCellHighlightMap = (
  sheetData: ValidAny[][],
  headerRowIndex: number,
  headerColumnMap: Map<string, number>,
  extractedRecords: ExtractedRecord[],
  rowIndexMode: RowIndexMode = "relative",
  targetToSourceMap?: Map<string, string>
): CellHighlightMap => {
  const map: CellHighlightMap = new Map();

  if (!extractedRecords || sheetData.length === 0) return map;

  const resolveColumn = createColumnResolver(headerColumnMap);

  for (const record of extractedRecords) {
    if (!record.extracted_fields) continue;

    const sheetRow = resolveSheetRow(record, headerRowIndex, rowIndexMode);
    if (sheetRow === null || sheetRow < 0 || sheetRow >= sheetData.length) continue;

    applyRecordHighlights(map, record, sheetRow, sheetData, headerColumnMap, resolveColumn, targetToSourceMap);
  }

  return map;
};

/**
 * Apply one record's highlights onto `map` at `sheetRow`.
 *
 * Lifted verbatim out of `buildCellHighlightMap` so the full-set and windowed
 * builders cannot drift: both call this, neither reimplements it.
 *
 * Algorithm:
 * 1. For each extracted field, find the column via header name matching
 * 2. Cross-reference with validation/transformation errors to set status
 * 3. Apply record-level errors (no field named) to the first mapped cell
 * 4. Handle "orphan" errors -- errors for fields that failed extraction
 *    entirely (no ExtractedField exists) by reverse-looking up the source
 *    column via targetToSourceMap (built from the template's field_mappings)
 */
const applyRecordHighlights = (
  map: CellHighlightMap,
  record: ExtractedRecord,
  sheetRow: number,
  sheetData: ValidAny[][],
  headerColumnMap: Map<string, number>,
  resolveColumn: ColumnResolver,
  targetToSourceMap?: Map<string, string>
): void => {
  const fields = record.extracted_fields;
  if (!fields) return;

  // Build a lookup of errored fields for this record
  const recordErrors = buildErrorsByField(record);

  // Record-level errors (no field specified) — apply to the first cell
  const recordLevelErrors = recordErrors.byField.get(RECORD_LEVEL_KEY) || [];

  for (const field of fields) {
    // Find the column for this field (cached)
    const col = resolveColumn(field.source);
    if (col === null) continue;

    const key = `${sheetRow}:${col}`;
    const existing = map.get(key);

    // Determine status based on errors — try target_field, source.key, and normalized variants
    const fieldErrors = lookupFieldErrors(recordErrors, field.target_field, field.source?.key);
    let status: CellHighlight["status"] = "extracted";
    if (fieldErrors.some((e) => e.severity === "error")) {
      status = "error";
    } else if (fieldErrors.some((e) => e.severity === "warning")) {
      status = "warning";
    }

    if (existing) {
      // Merge — upgrade status if needed
      existing.fields.push({
        targetField: field.target_field,
        targetSchema: field.target_schema,
        extractedValue: field.extracted_value,
      });
      existing.errors.push(...fieldErrors);
      if (status === "error" || (status === "warning" && existing.status === "extracted")) {
        existing.status = status;
      }
    } else {
      map.set(key, {
        status,
        fields: [
          {
            targetField: field.target_field,
            targetSchema: field.target_schema,
            extractedValue: field.extracted_value,
          },
        ],
        errors: [...fieldErrors],
      });
    }
  }

  // Apply record-level errors (those without a specific field) to the first
  // mapped cell in this row so they're still visible in the preview
  if (recordLevelErrors.length > 0) {
    const firstField = fields.find((f) => resolveColumn(f.source) !== null);
    if (firstField) {
      const col = resolveColumn(firstField.source)!;
      const key = `${sheetRow}:${col}`;
      const existing = map.get(key);
      if (existing) {
        existing.errors.push(...recordLevelErrors);
        if (recordLevelErrors.some((e) => e.severity === "error")) {
          existing.status = "error";
        } else if (existing.status === "extracted" && recordLevelErrors.some((e) => e.severity === "warning")) {
          existing.status = "warning";
        }
      }
    }
  }

  // Handle "orphan" errors — errors for fields that failed extraction entirely
  // (e.g. claimant_birth_date failed to parse from "Age Band" column).
  // These have an error.field but NO matching ExtractedField, so the loop above
  // never matched them.
  //
  // Resolution order for finding the source column:
  //  1. error.source  (full ExtractionLocation from backend — most reliable)
  //  2. targetToSourceMap  (template field_mappings or extracted records)
  {
    const extractedTargetFields = new Set(fields.map((f) => f.target_field));

    for (const [errField, errors] of recordErrors.byField) {
      if (errField === RECORD_LEVEL_KEY) continue;
      // Skip if this error was already matched to an extracted field
      if (extractedTargetFields.has(errField)) continue;

      // 1. Try the error's own ExtractionLocation (key or coordinates)
      const errorSource = errors.find((e) => e.source)?.source;
      let col: number | null = null;

      if (errorSource) {
        col = resolveColumn(errorSource);
      }

      // 2. Fall back to targetToSourceMap (template field_mappings)
      if (col === null || col === undefined) {
        const sourceHeader = targetToSourceMap?.get(errField);
        if (sourceHeader) {
          col = headerColumnMap.get(normalizeHeader(sourceHeader)) ?? null;
        }
      }

      if (col === null || col === undefined) continue;

      const key = `${sheetRow}:${col}`;
      const existing = map.get(key);

      const status: CellHighlight["status"] = errors.some((e) => e.severity === "error") ? "error" : "warning";

      if (existing) {
        existing.errors.push(...errors);
        if (status === "error" || (status === "warning" && existing.status === "extracted")) {
          existing.status = status;
        }
      } else {
        map.set(key, {
          status,
          fields: [
            {
              targetField: errField,
              targetSchema: fields[0]?.target_schema,
              extractedValue: sheetData[sheetRow]?.[col] ?? "",
            },
          ],
          errors: [...errors],
        });
      }
    }
  }
};

// =============================================================================
// Internal Helpers
// =============================================================================

/** Resolve the spreadsheet row for a record based on its source metadata */
const resolveSheetRow = (
  record: ExtractedRecord,
  headerRowIndex: number,
  rowIndexMode: RowIndexMode
): number | null => {
  if (!record.extracted_fields || record.extracted_fields.length === 0) return null;

  // Use the first field's source to determine row
  for (const field of record.extracted_fields) {
    const source = field.source;
    if (!source) continue;

    // Prefer line_item_row_index
    if (source.line_item_row_index !== null && source.line_item_row_index !== undefined) {
      return rowIndexMode === "relative" ? headerRowIndex + 1 + source.line_item_row_index : source.line_item_row_index;
    }

    // Fall back to coordinates
    if (source.coordinates && source.coordinates.length > 0) {
      return source.coordinates[0].y;
    }
  }

  return null;
};

/**
 * Build a map of field key → errors for a single record.
 *
 * Errors are keyed by their `field` property (which may be a target field name
 * OR a source header name depending on the backend). Errors without a `field`
 * are stored under the `__record__` key so they can be applied to the entire row.
 *
 * Each entry carries an optional `source` ExtractionLocation from the backend
 * error, which the orphan-error handler can use to resolve the column directly.
 */
const RECORD_LEVEL_KEY = "__record__";

interface FieldError {
  message: string;
  severity: "error" | "warning";
  /** Full extraction location from the backend error (transformation errors only) */
  source?: ExtractionLocation;
}

interface ErrorsByFieldResult {
  /** Exact-key lookup map (target_field or source key → errors) */
  byField: Map<string, FieldError[]>;
  /** Normalized-key lookup map (for fuzzy matching when exact fails) */
  byNormalized: Map<string, FieldError[]>;
}

const buildErrorsByField = (record: ExtractedRecord): ErrorsByFieldResult => {
  const byField = new Map<string, FieldError[]>();

  const addErrors = (errors: ExtractedRecordError[] | undefined, severity: "error" | "warning") => {
    if (!errors) return;
    for (const err of errors) {
      // Store field-less errors as record-level so they still surface
      const key = err.field || RECORD_LEVEL_KEY;
      const existing = byField.get(key) || [];
      existing.push({ message: err.message, severity, source: err.source });
      byField.set(key, existing);
    }
  };

  addErrors(record.validation_errors, "error");
  addErrors(record.transformation_errors, "warning");

  // Pre-build normalized lookup — avoids O(n) scan in lookupFieldErrors
  const byNormalized = new Map<string, FieldError[]>();
  for (const [key, errors] of byField) {
    if (key === RECORD_LEVEL_KEY) continue;
    byNormalized.set(normalizeHeader(key), errors);
  }

  return { byField, byNormalized };
};

/**
 * Look up errors for a specific extracted field.
 *
 * Tries multiple strategies to match an error to a field:
 *  1. Exact match on target_field (e.g. "date_of_birth")
 *  2. Exact match on source.key (the original header, e.g. "Date of Birth")
 *  3. Normalized match on source.key (lowercased / trimmed) — O(1) via pre-built map
 */
const lookupFieldErrors = (errors: ErrorsByFieldResult, targetField: string, sourceKey?: string): FieldError[] => {
  // 1. Exact target field match
  const byTarget = errors.byField.get(targetField);
  if (byTarget && byTarget.length > 0) return byTarget;

  if (sourceKey) {
    // 2. Exact source key match
    const bySource = errors.byField.get(sourceKey);
    if (bySource && bySource.length > 0) return bySource;

    // 3. Normalized source key match (O(1) instead of O(n) scan)
    const byNorm = errors.byNormalized.get(normalizeHeader(sourceKey));
    if (byNorm && byNorm.length > 0) return byNorm;
  }

  return [];
};

/**
 * Find the spreadsheet row for a specific ExtractedRecord.
 * Used by the drawer to scroll to the right row.
 */
export const getSheetRowForRecord = (
  record: ExtractedRecord,
  headerRowIndex: number,
  rowIndexMode: RowIndexMode = "relative"
): number | null => {
  return resolveSheetRow(record, headerRowIndex, rowIndexMode);
};
